/**
 * B6 · Un nodo del camino de niveles.
 *
 * ## Tres reglas que este componente no puede romper
 *
 * 1. **El estado NUNCA se comunica solo por color.** Cada nodo lleva icono y
 *    texto además del énfasis cromático: un candado, un play, «12 de 24», un
 *    check o un diploma. Quien no distingue colores tiene que poder jugar.
 * 2. **Ni una animación en JavaScript.** El pulso del nodo disponible es un
 *    `@keyframes` de `styles/global.css`, porque el guard global de
 *    `prefers-reduced-motion` neutraliza duraciones CSS y **no alcanzaría** a
 *    un `requestAnimationFrame`. Por eso este archivo no menciona la media
 *    query: hacerlo significaría que la animación se le escapó al guard (hay un
 *    test de invariante que lo comprueba).
 * 3. **Un nodo bloqueado explica por qué.** `blockedBy` es la key del criterio
 *    que falló; se traduce a una frase accionable. Un candado mudo genera un
 *    ticket de soporte por alumno.
 */
import { type Component, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Award, Check, Flag, Lock, Play } from "lucide-solid";
import { cn, type StageStatus } from "@space/shared";
import { useI18n } from "@/providers/i18n-provider";

/** Frase accionable a partir de la key del criterio que bloqueó. */
function blockedReason(
  t: ReturnType<typeof useI18n>["t"],
  blockedBy: string | null | undefined,
): string {
  switch (blockedBy) {
    case "stage_completed": {
      return t(msg`Finish the previous level first.`);
    }
    case "placement_test": {
      return t(msg`Pass the placement test to jump straight to this level.`);
    }
    case "entitlement": {
      return t(msg`Complete the payment for this level to unlock it.`);
    }
    case "cohort_active": {
      return t(msg`You need an active enrolment in a running group.`);
    }
    default: {
      return t(msg`This level isn't open for you yet.`);
    }
  }
}

/** Etiqueta textual del estado (la señal NO cromática, siempre visible). */
function statusLabel(
  t: ReturnType<typeof useI18n>["t"],
  status: StageStatus,
  progressPct: number,
): string {
  switch (status) {
    case "locked": {
      return t(msg`Locked`);
    }
    case "available": {
      return t(msg`Start`);
    }
    case "in_progress": {
      const pct = Math.round(progressPct);
      return t(msg`${pct}% done`);
    }
    case "completed": {
      return t(msg`Completed`);
    }
    default: {
      return t(msg`Certified`);
    }
  }
}

const StatusIcon: Component<{ status: StageStatus; milestone?: boolean }> = (props) => (
  <Show when={!props.milestone} fallback={<Flag class="size-6" aria-hidden="true" />}>
    <Show when={props.status !== "locked"} fallback={<Lock class="size-6" aria-hidden="true" />}>
      <Show
        when={props.status !== "certified"}
        fallback={<Award class="size-6" aria-hidden="true" />}
      >
        <Show
          when={props.status !== "completed"}
          fallback={<Check class="size-6" aria-hidden="true" />}
        >
          <Play class="size-6" aria-hidden="true" />
        </Show>
      </Show>
    </Show>
  </Show>
);

/** Énfasis visual por estado. El SIGNIFICADO va en el icono y el texto. */
function toneFor(status: StageStatus): string {
  switch (status) {
    case "locked": {
      return "border-dashed border-border bg-muted text-muted-foreground opacity-70";
    }
    case "available": {
      return "border-accent bg-accent/10 text-accent-foreground";
    }
    case "in_progress": {
      return "border-accent bg-background text-foreground";
    }
    case "completed": {
      return "border-accent bg-accent text-accent-foreground";
    }
    default: {
      return "border-accent bg-accent text-accent-foreground";
    }
  }
}

export interface StageNodeProps {
  title: string;
  status: StageStatus;
  progressPct: number;
  blockedBy?: string | null;
  milestone?: boolean;
  /** Etiqueta configurable del nivel («Nivel 2», «Módulo 2»…). */
  stageLabel: string;
  testid?: string;
  onActivate?: () => void;
}

export const StageNode: Component<StageNodeProps> = (props) => {
  const { t } = useI18n();
  const locked = (): boolean => props.status === "locked";

  return (
    <button
      type="button"
      data-testid={props.testid}
      data-status={props.status}
      // `aria-disabled` en vez de `disabled`: el nodo bloqueado SIGUE siendo
      // pulsable a propósito, porque al pulsarlo es cuando explica qué falta.
      // Deshabilitarlo lo sacaría del foco y dejaría al alumno sin respuesta.
      aria-disabled={locked()}
      onClick={() => props.onActivate?.()}
      class={cn(
        "path-node flex w-full max-w-xs items-center gap-3 rounded-2xl border-2 p-4 text-left",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        toneFor(props.status),
        props.milestone && "path-node--milestone",
        `path-node--${props.status}`,
      )}
    >
      <span
        class="flex size-11 shrink-0 items-center justify-center rounded-full border border-current/30"
        aria-hidden="true"
      >
        <StatusIcon status={props.status} milestone={props.milestone} />
      </span>
      <span class="min-w-0 flex-1">
        <span class="block truncate text-xs font-medium uppercase tracking-wide opacity-80">
          {props.stageLabel}
        </span>
        <span class="block truncate font-semibold">{props.title}</span>
        <span class="block truncate text-sm opacity-90">
          {statusLabel(t, props.status, props.progressPct)}
        </span>
        <Show when={locked()}>
          <span class="mt-1 block text-xs opacity-90">{blockedReason(t, props.blockedBy)}</span>
        </Show>
      </span>
    </button>
  );
};
